import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiRequest, getAccessToken, setAccessToken } from '../api/apiClient';
import { loadHeritageProfile, saveHeritageProfile } from '../services/heritageProfileService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const buildFrontendUser = (backendUser, heritageProfile = null) => {
    const roleUpper = (backendUser?.role || '').toString().toUpperCase();
    const isAdmin = roleUpper === 'ADMIN';
    const isOrgRep = roleUpper === 'ORG_REP';
    const userId = backendUser?.id ?? null;
    const profile =
      heritageProfile ?? (userId ? loadHeritageProfile(userId) : null);

    let role = 'user';
    let permissions = ['read'];
    if (isAdmin) {
      role = 'admin';
      permissions = ['read', 'write', 'delete', 'manage_users', 'manage_content', 'manage_settings'];
    } else if (isOrgRep) {
      role = 'org_rep';
      permissions = ['read', 'write'];
    }

    return {
      id: userId,
      email: backendUser?.email ?? null,
      name: backendUser?.fullName ?? '',
      avatar: backendUser?.email
        ? `https://ui-avatars.com/api/?name=${encodeURIComponent(backendUser.email)}&background=random`
        : `https://ui-avatars.com/api/?name=User&background=random`,
      joinDate: new Date().toISOString(),
      role,
      backendRole: roleUpper,
      organizationId: backendUser?.organizationId ?? null,
      organizationName: backendUser?.organizationName ?? '',
      permissions,
      heritageProfile: profile,
    };
  };

  // Load persisted auth from localStorage on mount.
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('gadkot_user');
      const storedToken = getAccessToken();
      if (storedUser && storedToken) {
        const parsed = JSON.parse(storedUser);
        const heritageProfile =
          parsed.heritageProfile ?? loadHeritageProfile(parsed.id);
        setUser({ ...parsed, heritageProfile: heritageProfile ?? parsed.heritageProfile ?? null });
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save user to localStorage whenever it changes.
  useEffect(() => {
    try {
      if (user) localStorage.setItem('gadkot_user', JSON.stringify(user));
      else localStorage.removeItem('gadkot_user');
    } catch {
      // ignore
    }
  }, [user]);

  const login = async (email, password) => {
    setIsLoading(true);
    try {
      const json = await apiRequest('/api/v1/auth/login', {
        method: 'POST',
        body: { email, password }
      });

      if (!json?.accessToken || !json?.user) {
        return { success: false, error: 'Invalid login response' };
      }

      setAccessToken(json.accessToken);
      setUser(buildFrontendUser(json.user));
      return { success: true };
    } catch (error) {
      return { success: false, error: error?.message || 'Login failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async ({ name, email, password, referralCode }) => {
    setIsLoading(true);
    try {
      const json = await apiRequest('/api/v1/auth/register', {
        method: 'POST',
        body: { fullName: name, email, password, referralCode: referralCode || undefined },
      });

      if (!json?.accessToken || !json?.user) {
        return { success: false, error: 'Invalid registration response' };
      }

      setAccessToken(json.accessToken);
      setUser(buildFrontendUser(json.user));
      return { success: true, userId: json.user?.id ?? null };
    } catch (error) {
      return { success: false, error: error?.message || 'Registration failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setAccessToken(null);
  };

  const updateProfile = (updates) => {
    setUser((prev) => {
      if (!prev) return prev;
      let next = { ...prev, ...updates };
      if (updates.heritageProfile && prev.id) {
        const saved = saveHeritageProfile(prev.id, updates.heritageProfile);
        next = { ...next, heritageProfile: saved };
      }
      return next;
    });
  };

  // Role-based access control functions
  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isOrgRep = () => {
    return user?.role === 'org_rep' && user?.organizationId != null;
  };

  const canAccessAdmin = () => {
    return isAdmin() || hasPermission('manage_content');
  };

  const requireAuth = () => {
    if (!user) {
      throw new Error('Authentication required');
    }
  };

  const requireAdmin = () => {
    if (!isAdmin()) {
      throw new Error('Admin access required');
    }
  };

  const value = {
    user,
    isLoading,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user,
    hasRole,
    hasPermission,
    isAdmin,
    isOrgRep,
    canAccessAdmin,
    requireAuth,
    requireAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
